/**
 * Staff-only, full-bleed view of the client's design approval trail for one portal
 * project — opened by "Pop out to window" from Project Hub › Files so a PM can keep
 * it on a second monitor.
 *
 * Hard requirement: same component, same builder, same client data path as the
 * public portal (portal mirror grouping — never live projectFileStreams).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { ExternalLink } from 'lucide-react';
import { db } from '../utils/firebase';
import Logo from '../components/ui/Logo';
import PortalApprovalTrail, { PortalApprovalTrailBoundary } from '../components/portal/PortalApprovalTrail';
import { subscribePortalBundlesForDeal, subscribePortalReleasesForDeal } from '../utils/portalFileReleaseService';
import { buildClientPortalTrailInput } from '../utils/portalApprovalTrailSource';
import { groupPortalDealApprovals, type PortalApprovalTargetGroup, emptyPortalApprovalTargetGroup } from '../utils/portalDeliverableApprovalService';
import { parsePortalProjectRow } from '../utils/portalProjectView';
import type { ClientPortal, PortalFileRelease, PortalProject, PortalReleaseBundle } from '../types';
import './PortalTrailPreviewPage.css';

const PortalTrailPreviewPage: React.FC = () => {
  const { portalId = '', dealId = '' } = useParams<{ portalId: string; dealId: string }>();
  const [releases, setReleases] = useState<PortalFileRelease[]>([]);
  const [bundles, setBundles] = useState<PortalReleaseBundle[]>([]);
  const [legacy, setLegacy] = useState<PortalApprovalTargetGroup>(emptyPortalApprovalTargetGroup);
  const [portalMeta, setPortalMeta] = useState<{ name: string; slug: string | null; projectName: string } | null>(null);
  const [portalProject, setPortalProject] = useState<PortalProject | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!portalId || !dealId) return;
    const unsubR = subscribePortalReleasesForDeal(portalId, dealId, rows => {
      setReleases(rows);
      setLoaded(true);
    });
    const unsubB = subscribePortalBundlesForDeal(portalId, dealId, setBundles);
    const unsubP = onSnapshot(doc(db, 'clientPortals', portalId), snap => {
      const data = (snap.data() || {}) as Record<string, unknown>;
      const rawProjects = data.projects;
      const rawProject =
        rawProjects && typeof rawProjects === 'object' ? (rawProjects as Record<string, unknown>)[dealId] : undefined;
      const project =
        rawProject && typeof rawProject === 'object'
          ? parsePortalProjectRow(dealId, rawProject as Record<string, unknown>)
          : parsePortalProjectRow(dealId, data);
      const root = parsePortalProjectRow(dealId, data);
      setPortalProject(project);
      setLegacy(
        groupPortalDealApprovals({
          plansApproval: project?.plansApproval ?? root.plansApproval,
          plansApprovalHistory: project?.plansApprovalHistory ?? root.plansApprovalHistory,
          modelApproval: project?.modelApproval ?? root.modelApproval,
          modelApprovalHistory: project?.modelApprovalHistory ?? root.modelApprovalHistory,
          conceptApproval: project?.conceptApproval ?? root.conceptApproval,
          conceptApprovalHistory: project?.conceptApprovalHistory ?? root.conceptApprovalHistory,
        } as ClientPortal),
      );
      const name = String(data.clientCompany || data.clientName || '').trim();
      setPortalMeta({
        name,
        slug: typeof data.slug === 'string' && data.slug ? data.slug : null,
        projectName: project?.projectName || root.projectName || '',
      });
    });
    return () => {
      unsubR();
      unsubB();
      unsubP();
    };
  }, [portalId, dealId]);

  useEffect(() => {
    const prev = document.title;
    document.title = portalMeta?.name ? `Client view · ${portalMeta.name}` : 'Client view';
    return () => {
      document.title = prev;
    };
  }, [portalMeta?.name]);

  const portalUrl = useMemo(
    () => (portalMeta?.slug ? `/portal/${portalMeta.slug}${dealId ? `?project=${encodeURIComponent(dealId)}` : ''}` : null),
    [portalMeta?.slug, dealId],
  );

  // Identical builder input as ClientPortalSplash — portal mirror only.
  const trailInput = useMemo(
    () =>
      buildClientPortalTrailInput({
        releases,
        bundles,
        portalProject,
        legacyApprovals: legacy,
      }),
    [releases, bundles, portalProject, legacy],
  );

  return (
    <div className="ptp-page">
      <header className="ptp-head">
        <Logo size="small" />
        <div className="ptp-head-text">
          <span className="ptp-kicker">Client view · live</span>
          <h1 className="ptp-title">
            {portalMeta?.name || 'Client portal'}
            {portalMeta?.projectName ? <span className="ptp-project"> · {portalMeta.projectName}</span> : null}
          </h1>
        </div>
        {portalUrl ? (
          <a className="ptp-open" href={portalUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={13} aria-hidden /> Open portal
          </a>
        ) : null}
      </header>
      <main className="ptp-main client-portal-theme">
        {!portalId || !dealId ? (
          <p className="ptp-note">Missing portal or project id.</p>
        ) : !loaded ? (
          <p className="ptp-note">Loading…</p>
        ) : releases.length === 0 ? (
          <p className="ptp-note">Nothing has been released to this client yet — the trail is hidden on their portal.</p>
        ) : (
          <PortalApprovalTrailBoundary fallback={<p className="ptp-note">The approval trail could not be rendered for this portal.</p>}>
            <PortalApprovalTrail {...trailInput} readOnly onOpenItem={item => window.open(item.releasedUrl, '_blank', 'noopener,noreferrer')} />
          </PortalApprovalTrailBoundary>
        )}
      </main>
    </div>
  );
};

export default PortalTrailPreviewPage;
